import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useDispatch, useSelector } from "react-redux";
import { NOTIFICATION } from "../config/constants";
import {
  markAllNotificationsReadInState,
  setNotificationReadState,
} from "../redux/slice/notificationSlice";

const NOTIFICATION_KEY = ["notifications"];

const invalidateNotifications = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY });
  queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
  if (id) queryClient.invalidateQueries({ queryKey: ["notification", id] });
};

export const useGetAllNotifications = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...NOTIFICATION_KEY, params],
    queryFn: async ({ signal }) => {
      const response = await Get(NOTIFICATION.get, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetUnreadNotificationCount = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["notifications-unread", params],
    queryFn: async () => {
      const response = await Get(NOTIFICATION.getUnreadAdminNotifications, token, params);
      return response?.data?.count || 0;
    },
    enabled: !!token,
  });
};

export const useGetNotificationById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["notification", id],
    queryFn: async () => {
      const response = await Get(NOTIFICATION.getOne + id, token);
      return response?.data?.notification;
    },
    enabled: !!token && !!id,
  });
};

export const useAddNotification = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (payload) => {
      const response = await Post(NOTIFICATION.create, payload, token);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create notification");
    },
    onSuccess: () => invalidateNotifications(queryClient),
  });
};

export const useUpdateNotification = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const response = await Post(NOTIFICATION.update + id, payload, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update notification");
    },
    onSuccess: (_data, variables) => invalidateNotifications(queryClient, variables?.id),
  });
};

export const useToggleNotificationRead = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async ({ id, isRead }) => {
      const response = await Post(NOTIFICATION.markAsRead + id, { isRead }, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update notification status");
    },
    onMutate: async ({ id, isRead }) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEY });
      dispatch(setNotificationReadState({ id, isRead }));

      const previousQueries = queryClient.getQueriesData({ queryKey: NOTIFICATION_KEY });
      previousQueries.forEach(([key, data]) => {
        if (!data?.docs) return;
        queryClient.setQueryData(key, {
          ...data,
          docs: data.docs.map((item) =>
            item._id === id ? { ...item, isRead } : item
          ),
        });
      });

      return { previousQueries };
    },
    onSuccess: (_data, variables) => {
      dispatch(setNotificationReadState({ id: variables.id, isRead: variables.isRead }));
      invalidateNotifications(queryClient, variables.id);
    },
    onError: (_error, variables, context) => {
      context?.previousQueries?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      dispatch(
        setNotificationReadState({
          id: variables.id,
          isRead: !variables.isRead,
        })
      );
      invalidateNotifications(queryClient, variables.id);
    },
  });
};

export const useMarkNotificationRead = () => {
  const { mutate, mutateAsync, ...rest } = useToggleNotificationRead();
  return {
    ...rest,
    mutate: (id, options) => mutate({ id, isRead: true }, options),
    mutateAsync: (id, options) => mutateAsync({ id, isRead: true }, options),
  };
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.userToken);

  return useMutation({
    mutationFn: async (payload = {}) => {
      const response = await Post(NOTIFICATION.markAllAsRead, payload, token);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to mark all notifications as read");
    },
    onMutate: async (payload) => {
      if (payload?.source !== "NOTICE") {
        dispatch(markAllNotificationsReadInState());
      }
    },
    onSuccess: (_data, payload) => {
      if (payload?.source !== "NOTICE") {
        dispatch(markAllNotificationsReadInState());
      }
      invalidateNotifications(queryClient);
    },
    onError: () => {
      invalidateNotifications(queryClient);
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Post(NOTIFICATION.delete + id, {}, token);
      if (response?.data?.status !== false && response?.status !== false) {
        return response?.data || response;
      }
      throw new Error(
        response?.data?.message || response?.message || "Failed to delete notification"
      );
    },
    onSuccess: () => invalidateNotifications(queryClient),
  });
};
